import React, {useState} from 'react';
import {getArchivedTicket, getOpenTickets} from '../Api/tickets';
import {useQuery} from 'react-query';
import {TicketFromApi} from '../../../app/interfaces/ticket';
import {useIntl} from 'react-intl';
import {useLang} from '../../../_metronic/i18n/Metronici18n';
import {useGetMe} from '../../../app/hooks/useGetMe';
import {Link, useNavigate} from 'react-router-dom';
import {getStatusColor, getStatusTextColor, getStatusLabel} from '../Helpers/stateHelper';
import {DateTime} from 'luxon';
import Loading from '../../../app/common/components/Loading';
import Level3Title from '../../../app/common/components/title/Level3Title';
import TableSearchBar from '../../../app/common/components/table/TableSearchBar';
import PaginationItem from '../../../app/common/components/table/pagination/PaginationItem';
import PaginationDetail from '../../../app/common/components/table/pagination/PaginationDetail';
import TablePageSizeSelector from '../../../app/common/components/table/TablePageSizeSelector';
import useIsMobile from '../../../app/common/components/table/useIsMobile';
import statusBadgeStyles from './TicketStatusBadge.module.css';
import styles from './TicketList.module.scss';
import clsx from 'clsx';

type TicketRow = TicketFromApi & {type?: string};

const paginateItems = (currentPage: number, lastPage: number): (string | number)[] | null => {
  if (!currentPage || !lastPage) {
    return null;
  }
  const items: (string | number)[] = [1];
  if (currentPage === 1 && lastPage === 1) {
    return items;
  }
  if (currentPage > 3) {
    items.push('...');
  }
  const r = 1;
  const r1 = currentPage - r;
  const r2 = currentPage + r;
  for (let i = r1 > 2 ? r1 : 2; i <= Math.min(lastPage, r2); i++) {
    items.push(i);
  }
  if (r2 + 1 < lastPage) {
    items.push('...');
  }
  if (r2 < lastPage) {
    items.push(lastPage);
  }
  return items;
};

export type OpenTicketFilter = 'all' | 'nouveau' | 'pris-en-charge';

interface TicketListProps {
  archived: boolean;
  limit?: number;
  withSearchBar?: boolean;
  customLinkTitle?: string;
  customLinkRoute?: string;
  hideIfEmpty?: boolean;
  openFilter?: OpenTicketFilter;
  onOpenFilterChange?: (filter: OpenTicketFilter) => void;
  filtersRightContent?: React.ReactNode;
  headerRightContent?: React.ReactNode;
}

const TicketList = ({
  archived,
  customLinkTitle,
  customLinkRoute,
  limit = 0,
  withSearchBar = true,
  hideIfEmpty = false,
  openFilter: openFilterProp,
  onOpenFilterChange,
  filtersRightContent,
  headerRightContent,
}: TicketListProps): JSX.Element => {
  const intl = useIntl();
  const lang = useLang();
  const customer = useGetMe();
  const isMobile = useIsMobile(768);
  const fetchMethod = archived ? getArchivedTicket : getOpenTickets;
  // const queryKey = 'get-ticket-' + archived;

  const [internalOpenFilter, setInternalOpenFilter] = useState<OpenTicketFilter>('all');
  const openFilter = openFilterProp ?? internalOpenFilter;
  const setOpenFilter = (f: OpenTicketFilter) => {
    setInternalOpenFilter(f);
    onOpenFilterChange?.(f);
  };

  const {isLoading, data: ticketList} = useQuery<TicketFromApi[]>(
    ['tickets', archived ? 'archived' : 'open'],
    // [queryKey],
    async () => fetchMethod()
  );

  const normalizeState = (s: string | null): string => (s ?? '').trim().toLowerCase();

  const matchesFilter = (ticket: TicketFromApi): boolean => {
    if (archived || openFilter === 'all') return true;
    const state = normalizeState(ticket.state);
    if (openFilter === 'nouveau') return state === 'nouveau';
    if (openFilter === 'pris-en-charge') return state === 'pris en charge';
    return true;
  };

  const allOpenCount = (ticketList ?? []).length;
  const nouveauCount = (ticketList ?? []).filter(
    (t) => normalizeState(t.state) === 'nouveau'
  ).length;
  const priseEnChargeCount = (ticketList ?? []).filter(
    (t) => normalizeState(t.state) === 'pris en charge'
  ).length;

  const filterButtons: {
    key: 'all' | 'nouveau' | 'pris-en-charge';
    label: string;
    count: number;
  }[] = [
    {key: 'all', label: intl.formatMessage({id: 'TICKETS.FILTER.ALL'}), count: allOpenCount},
    {key: 'nouveau', label: intl.formatMessage({id: 'TICKETS.FILTER.NEW'}), count: nouveauCount},
    {
      key: 'pris-en-charge',
      label: intl.formatMessage({id: 'TICKETS.FILTER.IN_PROGRESS'}),
      count: priseEnChargeCount,
    },
  ];
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number>(5);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const parseOuverture = (dateStr: string): DateTime => {
    const iso = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
    return DateTime.fromISO(iso);
  };

  const formatOuverture = (dateStr: string): string => {
    const dt = parseOuverture(dateStr);
    if (!dt.isValid) return dateStr;
    return dt.setLocale(lang).toFormat(intl.formatMessage({id: 'DASHBOARD.TICKETS.DATE_FORMAT'}));
  };

  const formatRelativeUpdate = (dateStr: string): string | null => {
    const dt = parseOuverture(dateStr);
    if (!dt.isValid) return null;
    return dt.setLocale(lang).toRelative({base: DateTime.now()});
  };

  function navigateToTicket(ticket: TicketFromApi, customer: any, navigate: any) {
    const customerId = ticket.bu === null ? customer.id.toString() : ticket.bu.toString();

    navigate(`/support/${ticket.numDossier.toString()}?customerId=${customerId}`, {
      state: {
        customerId: customerId,
        ticketId: ticket.numDossier.toString(),
      },
    });
  }

  const handleRowClick = (ticket: TicketFromApi) => {
    if (ticket.state !== null && ticket.state.toString() === 'nouveau') {
      alert(intl.formatMessage({id: 'TICKETS.ALERT.NOT_TAKEN'}));
    } else {
      navigateToTicket(ticket, customer, navigate);
    }
  };

  if (hideIfEmpty && (!ticketList || ticketList.length === 0)) {
    return <div></div>;
  }

  const filteredList = (ticketList ?? []).filter(matchesFilter);

  const searched = searchQuery.trim()
    ? filteredList.filter((t) => {
        const q = searchQuery.toLowerCase();
        return (
          t.numDossier.toString().toLowerCase().includes(q) ||
          (t.titre ?? '').toLowerCase().includes(q) ||
          (t.site ?? '').toLowerCase().includes(q) ||
          (t.state ?? '').toLowerCase().includes(q)
        );
      })
    : filteredList;

  const sorted = [...searched].sort((a, b) => Number(b.numDossier) - Number(a.numDossier));

  const limited = limit && limit > 0 ? sorted.slice(0, limit) : sorted;

  const paginate = withSearchBar && !limit;
  const totalPages = paginate ? Math.max(1, Math.ceil(limited.length / pageSize)) : 1;
  const currentPage = Math.min(pageIndex, totalPages - 1);
  const visibleRows = paginate
    ? limited.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    : limited;

  const emptyMessage = intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.TICKET_EMPTY'});

  return (
    <>
      <div className="card">
        {(withSearchBar || (customLinkTitle && customLinkRoute) || headerRightContent) && (
          <div className="card-header border-0 pt-6 d-flex align-items-center justify-content-between">
            <div className="card-title">
              <Level3Title
                title={intl.formatMessage({
                  id: archived
                    ? 'TICKETS.DASHBOARD.TITLE.ARCHIVED'
                    : 'TICKETS.DASHBOARD.TITLE.OPEN',
                })}
              />
            </div>
            {customLinkTitle && customLinkRoute && (
              <div className="card-toolbar">
                <Link to={customLinkRoute} title={customLinkTitle}>
                  {customLinkTitle}
                </Link>
              </div>
            )}
            {headerRightContent && <div className="card-toolbar">{headerRightContent}</div>}
          </div>
        )}

        {(!archived || withSearchBar || filtersRightContent) && (
          <div
            className={clsx(
              'd-flex justify-content-between align-items-center px-9 pt-3 pb-2 flex-wrap gap-2',
              styles.filtersRow
            )}
          >
            <div className="d-flex gap-2 flex-wrap">
              {!archived &&
                filterButtons.map((btn) => {
                  const isActive = openFilter === btn.key;
                  return (
                    <button
                      key={btn.key}
                      type="button"
                      onClick={() => setOpenFilter(btn.key)}
                      className={`btn btn-sm rounded-pill d-inline-flex align-items-center px-4 py-1 ${
                        isActive ? 'text-primary' : 'text-muted'
                      }`}
                      style={{
                        backgroundColor: isActive ? '#eaf1fd' : '#ffffff',
                        border: `1px solid ${isActive ? '#c7dbeb' : 'var(--bs-border-color)'}`,
                      }}
                    >
                      <span style={{fontWeight: 600}}>{btn.label}</span>
                      <span
                        className={`ms-2 d-inline-flex align-items-center justify-content-center rounded-circle ${
                          isActive ? 'text-primary' : 'text-muted'
                        }`}
                        style={{
                          minWidth: 20,
                          height: 20,
                          padding: '1px 6px 0 6px',
                          fontSize: 10.5,
                          fontWeight: 500,
                          backgroundColor: isActive ? '#c3d9f8' : '#e9ecef',
                        }}
                      >
                        {btn.count}
                      </span>
                    </button>
                  );
                })}
            </div>
            <div className={clsx('d-flex align-items-center gap-3', styles.searchGroup)}>
              {withSearchBar && (
                <TableSearchBar
                  fullWidth={isMobile}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPageIndex(0);
                  }}
                />
              )}
              {filtersRightContent}
            </div>
          </div>
        )}

        <div className="card-body py-4 position-relative">
          {isLoading && (
            <div className="table-loading-message">
              <Loading />
            </div>
          )}

          {visibleRows.length === 0 && !isLoading && (
            <div className="text-center text-muted py-6">{emptyMessage}</div>
          )}

          <div className="d-flex flex-column">
            {visibleRows.map((ticket, index) => {
              const stateText = getStatusLabel(ticket.state, intl);
              const statusColor = getStatusColor(ticket.state);
              const type = (ticket as TicketRow).type;
              const typeLabel =
                type === 'technical'
                  ? intl.formatMessage({id: 'DASHBOARD.TICKETS.TYPE.TECHNICAL'})
                  : intl.formatMessage({id: 'DASHBOARD.TICKETS.TYPE.ADMINISTRATIVE'});
              const updatedRelative = ticket.updateAt
                ? formatRelativeUpdate(ticket.updateAt)
                : null;

              return (
                <div
                  key={ticket.numDossier}
                  role="button"
                  onClick={() => handleRowClick(ticket)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className={clsx(styles.ticketRow, 'px-4 py-3 hoverable-row')}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      hoveredIndex === index ? '#eaf1fd' : index % 2 === 0 ? '#f7f9fc' : '#ffffff',
                    transition: 'background-color 0.15s ease-in-out',
                  }}
                >
                  <div className={styles.colNumber}>
                    <span className="text-muted fs-7">
                      {intl.formatMessage({id: 'TICKETS.NUMBER_PREFIX'})}{' '}
                      <span className="text-dark fw-semibold">{ticket.numDossier.toString()}</span>
                    </span>
                    <span
                      className="mt-2 d-inline-flex align-items-center text-gray-800 fs-8 px-2 py-1 rounded align-self-start"
                      style={{border: '1px solid #e4e6ef'}}
                    >
                      <i className="bi bi-chat-square-text me-2 text-gray-800" />
                      {typeLabel}
                    </span>
                  </div>

                  <div className={styles.colStatus}>
                    <span
                      className={statusBadgeStyles.statusBadge}
                      style={{
                        backgroundColor: statusColor,
                        color: getStatusTextColor(statusColor),
                      }}
                    >
                      {stateText}
                    </span>
                  </div>

                  <div className={styles.colTitle}>
                    <div className="fw-bold text-dark">{ticket.titre}</div>
                    <div className="d-flex flex-wrap gap-4 text-muted fs-7 mt-1">
                      <span className="d-inline-flex align-items-center">
                        <i className="bi bi-calendar me-2" />
                        {intl.formatMessage({id: 'DASHBOARD.TICKETS.OPENED_ON'})}{' '}
                        {formatOuverture(ticket.ouverture)}
                      </span>
                      {updatedRelative && (
                        <span className="d-inline-flex align-items-center">
                          <i className="bi bi-clock me-2" />
                          {intl.formatMessage({id: 'DASHBOARD.TICKETS.UPDATED'})} {updatedRelative}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.colSite}>
                    {ticket.siteName || ticket.site ? (
                      <>
                        <span className="text-dark fw-semibold fs-7 d-flex align-items-center overflow-hidden">
                          <i className="bi bi-geo-alt me-2 text-gray-600 flex-shrink-0" />
                          <span className="text-truncate d-block" title={ticket.siteName ?? '—'}>
                            {ticket.siteName ?? '—'}
                          </span>
                        </span>
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <span className="text-muted fs-8 text-uppercase">
                            {intl.formatMessage({id: 'TICKETS.SITE'})}
                          </span>
                          {ticket.site && (
                            <span className="text-muted fs-7">
                              {intl.formatMessage({id: 'TICKETS.NUMBER_PREFIX'})}{' '}
                              <span className="text-gray-700">{ticket.site}</span>
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted fs-7 d-flex align-items-center h-100">
                        <i className="bi bi-geo-alt me-2 text-gray-600 flex-shrink-0" />
                        <span>{intl.formatMessage({id: 'TICKETS.SITE.UNDEFINED'})}</span>
                      </span>
                    )}
                  </div>

                  <div className={styles.colChevron}>
                    <i className="bi bi-chevron-right text-muted" />
                  </div>
                </div>
              );
            })}
          </div>

          {paginate && limited.length > 0 && (
            <div className="d-flex flex-stack flex-wrap pt-5">
              <PaginationDetail
                pagination={{pageIndex: currentPage, pageSize}}
                totalNumber={limited.length}
              />
              <ul className="pagination">
                <PaginationItem
                  disabled={currentPage === 0}
                  content={<i className="previous"></i>}
                  action={() => setPageIndex(Math.max(0, currentPage - 1))}
                />
                {(paginateItems(currentPage + 1, totalPages) ?? []).map((item, key) => (
                  <PaginationItem
                    key={key}
                    disabled={item === '...'}
                    content={`${item}`}
                    action={() => {
                      if (typeof item === 'number') {
                        setPageIndex(item - 1);
                      }
                    }}
                    {...(item === currentPage + 1 && {active: true})}
                  />
                ))}
                <PaginationItem
                  disabled={currentPage >= totalPages - 1}
                  content={<i className="next"></i>}
                  action={() => setPageIndex(Math.min(totalPages - 1, currentPage + 1))}
                />
              </ul>
              <div className="d-flex justify-content-end fs-6 fw-semibold text-gray-700">
                <TablePageSizeSelector
                  pageSizeState={pageSize}
                  setPageSize={(selectedValue) => {
                    setPageSize(selectedValue);
                    setPageIndex(0);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketList;
